import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faXmark } from '@fortawesome/free-solid-svg-icons';
import './TableModal.css';
import type { Table, MenuItem } from '../../types';
import ModalShell from '../ModalShell/ModalShell';
import { useAutoFocus } from '../../hooks/useAutoFocus';
import { useTableModalState } from './useTableModalState';
import TableDetailsForm from './TableDetailsForm';
import TableProductsPanel from './TableProductsPanel';
import {
  getCopiedTableProducts,
  hasCopiedTableProducts,
  setCopiedTableProducts,
} from '../../utils/tableProductClipboard';
import { areTablesEqual, cloneTable } from '../../utils/tableState';
import { equalsNormalizedSearchText } from '../../utils/textSearch';
import { formatEuro } from '../../utils/currency';
import { useI18n } from '../../i18n/I18nContext';

export interface TableModalProps {
  table: Table | null;
  defaultTableColor: string;
  readOnly?: boolean;
  showPrices?: boolean;
  itemizePrintItems?: boolean;
  onSave: (table: Table) => void;
  onAutoSave?: (table: Table) => void;
  onClose: (saved: boolean) => void;
  showToast?: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  menuItems: MenuItem[];
}

const TableModal: React.FC<TableModalProps> = ({
  table,
  defaultTableColor,
  readOnly = false,
  showPrices = true,
  itemizePrintItems = false,
  onSave,
  onAutoSave,
  onClose,
  showToast,
  menuItems = [],
}) => {
  const { t } = useI18n();
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const newProductInputRef = useRef<HTMLInputElement | null>(null);
  const openedTableRef = useRef<Table | null>(null);
  const openedTableIdRef = useRef<string | null>(null);
  const effectiveAutoSave = readOnly ? undefined : onAutoSave;

  if (!table) return null;

  const {
    editedTable,
    newProductName,
    newProductNote,
    newProductQty,
    newProductHandling,
    newProductDelivered,
    newProductPaid,
    copiedProducts,
    dragOverIndex,
    setNewProductName,
    setNewProductNote,
    setNewProductQty,
    setNewProductHandling,
    setNewProductDelivered,
    setNewProductPaid,
    setCopiedProducts,
    buildPendingProduct,
    appendProduct,
    addPendingProduct,
    updateTable,
    resetTableColor,
    removeProduct,
    removeAllProducts,
    toggleHandling,
    toggleDelivered,
    togglePaid,
    editProductQty,
    editProductName,
    editProductNote,
    startRowDrag,
    dragOverRow,
    dropRow,
  } = useTableModalState({ table, menuItems, onAutoSave: effectiveAutoSave });

  useAutoFocus(newProductInputRef);

  useEffect(() => {
    if (editedTable.id !== table.id) {
      return;
    }

    if (openedTableIdRef.current === table.id) {
      return;
    }

    openedTableIdRef.current = table.id;
    openedTableRef.current = cloneTable(editedTable);
  }, [editedTable, table.id]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        handleOk();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editedTable, newProductName, newProductNote, newProductQty]);

  const orderTotal = (editedTable.products ?? []).reduce((sum, product) => {
    const unitPrice = typeof product.unitPrice === 'number' ? product.unitPrice : 0;
    return sum + unitPrice * product.qty;
  }, 0);
  const hasAnyPricedProduct = (editedTable.products ?? []).some((product) => typeof product.unitPrice === 'number');

  async function handleCopyProducts() {
    if (!editedTable.products || editedTable.products.length === 0) {
      showToast?.(t('table.noProductsToCopy'), 'error');
      return;
    }
    const copy = editedTable.products.map(product => ({ ...product }));
    setCopiedProducts(copy);
    setCopiedTableProducts(copy);
    try {
      const clipboardItems = editedTable.products
        .filter(product => !product.paid)
        .map(product => {
          const found = menuItems.find(item => equalsNormalizedSearchText(item.name, product.name || ''));
          return {
            id: product.itemId || (found ? found.id : product.name),
            name: product.name,
            unitPrice: typeof product.unitPrice === 'number' ? product.unitPrice : null,
            note: product.note ?? '',
            quantity: product.qty,
            handling: !!product.handling,
            delivered: !!product.delivered,
            paid: !!product.paid,
          };
        });
      const clipboardString =
        '[ ' +
        clipboardItems
          .map(item => {
            const safeName = String(item.name).replace(/"/g, '\\"');
            const safeId = String(item.id).replace(/"/g, '\\"');
            const safeNote = String(item.note ?? '').replace(/"/g, '\\"');
            return `{id: "${safeId}", name: "${safeName}", note: "${safeNote}", unitPrice: ${item.unitPrice == null ? 'null' : item.unitPrice}, quantity: ${item.quantity}, delivered: ${item.delivered}, paid: ${item.paid}}`;
          })
          .join(', ') +
        ' ]';
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardString);
      }
    } catch (err) {
      // ignore clipboard errors
    }
    showToast?.(t('table.productsCopied'), 'success');
  }

  async function handlePasteProducts() {
    const sharedCopiedProducts = getCopiedTableProducts();
    if (sharedCopiedProducts && sharedCopiedProducts.length > 0) {
      setCopiedProducts(sharedCopiedProducts);
      sharedCopiedProducts
        .map(product => ({ ...product, id: Date.now() + Math.random() }))
        .forEach(appendProduct);
      showToast?.(t('table.productsPasted'), 'success');
      return;
    }

    let itemsToPaste: any[] | null = null;
    try {
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        if (text.trim()) {
          try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) {
              itemsToPaste = parsed;
            }
          } catch {
            try {
              const parsed = Function(`"use strict"; return (${text});`)();
              if (Array.isArray(parsed)) {
                itemsToPaste = parsed;
              }
            } catch {
              // ignore parse errors
            }
          }
        }
      }
    } catch (err) {
      // ignore clipboard errors
    }

    if (itemsToPaste && itemsToPaste.length > 0) {
      itemsToPaste
        .map(item => ({
          id: Date.now() + Math.random(),
          itemId: (item.id || item.itemId || item.name) as string | number,
          name: item.name,
          unitPrice: typeof item.unitPrice === 'number' ? item.unitPrice : null,
          note: typeof item.note === 'string' && item.note.trim() ? item.note : undefined,
          qty: isNaN(parseInt(item.quantity)) || parseInt(item.quantity) < 1 ? 1 : parseInt(item.quantity),
          handling: !!item.handling,
          delivered: !!item.delivered,
          paid: !!item.paid,
        }))
        .forEach(appendProduct);
      showToast?.(t('table.productsPasted'), 'success');
      return;
    }

    showToast?.(t('table.noValidProductsToPaste'), 'error');
  }

  function handlePrintOrder() {
    if (!editedTable.products || editedTable.products.length === 0) {
      showToast?.(t('table.noProductsToPrint'), 'error');
      return;
    }
    const toPrint = editedTable.products.filter(product => !product.delivered);
    if (toPrint.length === 0) {
      showToast?.(t('table.noProductsToPrint'), 'error');
      return;
    }
    (window as Window & { __tableArrangementSkipPagehideSyncUntil?: number }).__tableArrangementSkipPagehideSyncUntil =
      Date.now() + 60_000;
    const win = window.open('', '_blank');
    if (!win) return;
    const doc = win.document;
    doc.write(`<html><head><title>${t('table.orderTitle')}</title>`);
    doc.write('<style>body { font-family: sans-serif; padding: 20px; } h1 { margin-top: 0; margin-bottom: 4px; } .order-person { margin-top: 0; margin-bottom: 16px; font-size: 14px; color: #555; } .order-footer-space { height: 2.8em; }</style>');
    doc.write('</head><body>');
    doc.write(`<h1>${editedTable.name}</h1>`);
    if (editedTable.person?.trim()) {
      doc.write(`<p class="order-person">${editedTable.person.trim()}</p>`);
    }
    let total = 0;
    let hasPricedProduct = false;
    toPrint.forEach(item => {
      const extra = item.note?.trim() ? ` <small>(${item.note})</small>` : '';
      const unitPrice = typeof item.unitPrice === 'number' ? item.unitPrice : null;
      const aggregatedLineTotal = showPrices && unitPrice != null ? unitPrice * item.qty : null;
      if (aggregatedLineTotal != null) {
        hasPricedProduct = true;
        total += aggregatedLineTotal;
      }
      if (itemizePrintItems) {
        const lineCount = Math.max(1, Number.isFinite(item.qty) ? item.qty : 1);
        const unitPriceMarkup =
          showPrices && unitPrice != null
            ? ` <strong style="float:right">${formatEuro(unitPrice)}</strong>`
            : '';
        for (let index = 0; index < lineCount; index += 1) {
          doc.write(`<p>${item.name}${extra}${unitPriceMarkup}</p>`);
        }
        return;
      }

      const priceMarkup =
        aggregatedLineTotal == null
          ? ''
          : ` <strong style="float:right">${formatEuro(aggregatedLineTotal)}</strong>`;
      doc.write(`<p>${item.qty} x ${item.name}${extra}${priceMarkup}</p>`);
    });
    if (showPrices && hasPricedProduct) {
      doc.write(`<hr /><p><strong>${t('table.total')}: ${formatEuro(total)}</strong></p>`);
    }
    doc.write('<div class="order-footer-space"></div>');
    doc.write('</body></html>');
    doc.close();
    win.print();
    win.close();
  }

  function handleOk() {
    if (readOnly) {
      onClose(false);
      return;
    }
    if (!editedTable.name.trim()) {
      showToast?.(t('table.tableNameRequired'), 'error');
      return;
    }
    const pendingProduct = buildPendingProduct();
    const tableToSave = pendingProduct
      ? {
          ...editedTable,
          products: editedTable.products ? [...editedTable.products, pendingProduct] : [pendingProduct],
        }
      : editedTable;
    onSave(tableToSave);
    showToast?.(t('table.updated'), 'success');
    onClose(true);
  }

  function handleClose(): void {
    if (!readOnly && openedTableRef.current && !areTablesEqual(openedTableRef.current, editedTable)) {
      onSave(cloneTable(editedTable));
    }
    onClose(false);
  }

  return (
    <ModalShell onClose={handleClose} ariaLabel={t('table.editTitle')} showCloseButton={false}>
      <button type="button" className="modal-close" onClick={handleClose} aria-label={t('table.closeAria')}>
        <FontAwesomeIcon icon={faXmark} />
      </button>
      <h3 style={{ marginTop: 0, marginBottom: 10 }}>{t('table.editTitle')}</h3>
      <div className="modal-autosave-note">{t('table.autosaveNote')}</div>
      {readOnly ? <div className="modal-readonly-note">{t('presence.tableReadonly')}</div> : null}
      <div className="print-order-controls">
        <button type="button" className="print-order-btn" onClick={handlePrintOrder}>
          <FontAwesomeIcon icon={faPrint} style={{ marginRight: 6 }} /> {t('table.printOrder')}
        </button>
        <span
          className="print-order-setting"
          title={t('table.itemizePrintHint')}
          aria-label={t('table.itemizePrintHint')}
        >
          <input type="checkbox" checked={itemizePrintItems} disabled readOnly />
          <span>{t('table.itemizePrint')}</span>
        </span>
      </div>
      <TableDetailsForm
        table={editedTable}
        defaultTableColor={defaultTableColor}
        readOnly={readOnly}
        nameInputRef={nameInputRef}
        onNameChange={value => updateTable({ name: value })}
        onPersonChange={value => updateTable({ person: value })}
        onTableColorChange={value => updateTable({ color: value })}
        onResetTableColor={resetTableColor}
      />
      <TableProductsPanel
        table={editedTable}
        newProductInputRef={newProductInputRef}
        readOnly={readOnly}
        showPrices={showPrices}
        canPaste={hasCopiedTableProducts() || Boolean(copiedProducts && copiedProducts.length > 0)}
        menuItems={menuItems}
        newProductName={newProductName}
        newProductNote={newProductNote}
        newProductQty={newProductQty}
        newProductHandling={newProductHandling}
        newProductDelivered={newProductDelivered}
        newProductPaid={newProductPaid}
        dragOverIndex={dragOverIndex}
        onNewProductNameChange={setNewProductName}
        onNewProductNoteChange={setNewProductNote}
        onNewProductQtyChange={setNewProductQty}
        onNewProductHandlingChange={setNewProductHandling}
        onNewProductDeliveredChange={setNewProductDelivered}
        onNewProductPaidChange={setNewProductPaid}
        onAddProduct={addPendingProduct}
        onCopyProducts={handleCopyProducts}
        onPasteProducts={handlePasteProducts}
        onRemoveAllProducts={removeAllProducts}
        onToggleHandling={toggleHandling}
        onToggleDelivered={toggleDelivered}
        onTogglePaid={togglePaid}
        onEditQty={editProductQty}
        onEditName={editProductName}
        onEditNote={editProductNote}
        onRemoveProduct={removeProduct}
        onDragStart={startRowDrag}
        onDragOver={dragOverRow}
        onDrop={dropRow}
      />
      <div className="modal-actions">
        {showPrices ? (
          <span className="modal-order-total">
            {t('table.total')}: {formatEuro(orderTotal)}
            {!hasAnyPricedProduct ? ` (${t('table.totalNoPricesHint')})` : ''}
          </span>
        ) : (
          <span />
        )}
        <button type="button" onClick={handleOk}>{readOnly ? t('table.close') : t('table.done')}</button>
      </div>
    </ModalShell>
  );
};

export default TableModal;


